import { forwardRef } from 'react'
import clsx from 'clsx'
import Link from 'next/link'
import Image from 'next/legacy/image'
import useDates from '@/hooks/dates'

// Layout
import DefaultContentLayout from '@/layouts/defaultContent'

// Hooks
import useTranslation from '@/hooks/translation'
import useTrips from '@/hooks/trips'
import useUser from '@/hooks/user'

// Components
import Button from '@/components/ui/button'
import Loader from '@/components/ui/loader'
import Trip from '@/components/Trip'
import Tabs from '@/components/ui/tabs'
import Head from '@/components/Head'
import DownloadAppPanel from '@/components/downloadapp/DownloadAppPanel'

// Assets
import TripPlaceholder from '@/assets/img/trip-car-placeholder.png'

// Utils
import { getServerSideAuthProps } from '@/utils/auth'
import { composeSSR } from '@/utils/ssr'
import useCarRentals from '@/hooks/car_rentals'

function CarRental({ rental, className }) {
  const { t } = useTranslation()
  const { formatDate } = useDates()
  const carOffer = rental?.car_offer
  // FIXME, handle timezone with rental locations
  const isPast = new Date(rental.booking_details.return_date) < new Date()

  return (
    <article
      className={clsx(
        'shadow rounded motion-safe:transition-all motion-safe:duration-100 hover:shadow-md h-full flex flex-col',
        className
      )}
    >
      <header className="relative rounded-t">
        <Image
          src={TripPlaceholder}
          width={339}
          height={200}
          layout="responsive"
          objectFit="cover"
          alt=""
        />
        <div className="absolute h-[100px] w-[100px] bg-white rounded-10 shadow-xl p-10 -bottom-20 left-20">
          <div className="relative w-full h-full">
            <Image
              layout="fill"
              objectFit="contain"
              src={rental?.booking_details?.example_model_url}
              alt=""
            />
          </div>
        </div>
      </header>
      <main className="bg-white rounded-b p-30 pt-20 flex-grow">
        <div className="flex items-center justify-end">
          <span
            className={clsx(
              'px-5 py-3 rounded text-14 mb-10 leading-none inline-block',
              {
                'text-red bg-red-20': rental.status === 'cancelled',
                'text-success bg-green-20':
                  rental.status === 'completed' && isPast,
                'text-primary bg-primary-20':
                  rental.status === 'completed' && !isPast,
              }
            )}
          >
            {rental.status === 'cancelled'
              ? t('components.trip.cancelled')
              : isPast
              ? t('components.trip.past')
              : t('components.trip.active')}
          </span>
        </div>
        {rental?.booking_details && (
          <div className="flex justify-between gap-4 items-baseline">
            <p className="text-17 flex-1">
              <span>
                {formatDate(
                  new Date(rental.booking_details.pickup_date),
                  'd MMM'
                )}
              </span>
              &nbsp;-&nbsp;
              <span>
                {formatDate(
                  new Date(rental.booking_details.return_date),
                  'd MMM'
                )}
              </span>
            </p>
            <p className="text-12 text-right">
              {rental?.booking_details?.pickup_location}
            </p>
          </div>
        )}
        <div className="flex gap-10 items-end mt-10">
          <h4>
            <p className="text-22 font-bold">
              {rental?.booking_details?.example_model}
            </p>
          </h4>
          <div className="relative w-50 h-30">
            <Image
              src={`https://www.bsp-auto.com/img/adm/l${carOffer.renter.id}.gif`}
              alt=""
              layout="fill"
              objectFit="contain"
            />
          </div>
        </div>
        <p className="text-dark-60 text-14">
          ou similaire avec {rental?.car_offer?.license_years} ans de permis
        </p>
      </main>
      {rental.voucher_link && (
        <footer className="p-30 pt-0">
          <Button
            color="primary"
            className="w-full"
            outline
            size="sm"
            href={rental.voucher_link}
          >
            Télécharger le récapitulatif
          </Button>
        </footer>
      )}
    </article>
  )
}

function Trips({ isPast, trips, rentals, isLoading }) {
  const { t } = useTranslation()

  // group and sort flights and car rentrals
  const allTrips = (trips ?? [])
    .map((trip) => ({
      type: 'flight',
      id: trip.id,
      date: trip.departureDate,
      trip,
    }))
    .concat(
      (rentals ?? []).map((rental) => ({
        type: 'car',
        id: rental.id,
        date: rental?.booking_details?.pickup_date,
        rental,
      }))
    )
    .sort((a, b) =>
      isPast
        ? new Date(b.date) - new Date(a.date)
        : new Date(a.date) - new Date(b.date)
    )

  return (
    <div className="mt-40">
      {isLoading ? (
        <div className="flex justify-center">
          <Loader />
        </div>
      ) : allTrips?.length > 0 ? (
        <section>
          <ul className="grid grid-cols-12 gap-30 items-stretch">
            {allTrips.map((trip) => (
              <li
                className="col-span-12 md:col-span-6 lg:col-span-4 p-0 flex"
                key={trip.id}
              >
                {trip.type === 'car' ? (
                  <CarRental rental={trip.rental} className="flex-1" />
                ) : (
                  <Link href={`/trips/${trip.id}`} className="block flex-1">
                    <Trip trip={trip.trip} className="h-full" />
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : (
        <section>
          <div className="flex flex-col items-center pt-20">
            <h2 className="text-18 font-bold mb-10">
              {!isPast
                ? t('pages.trips.emptyTitleFuture')
                : t('pages.trips.emptyTitlePast')}
            </h2>
            <Button href="/" plain>
              {t('pages.trips.emptyButton')}
            </Button>
          </div>
        </section>
      )}
    </div>
  )
}

const TabTitle = forwardRef(function TabTitleInner(
  { children, isSelected, className, ...props },
  forwardedRef
) {
  return (
    <div
      {...props}
      ref={forwardedRef}
      className={clsx(
        'border-b-2 mr-20 pb-15 cursor-pointer motion-safe:transition-all ease-in-out motion-safe:duration-100',
        {
          'border-primary text-primary': isSelected,
          'border-transparent text-dark-60 hover:border-dark-60': !isSelected,
        },
        className
      )}
    >
      {children}
    </div>
  )
})

function Bookings() {
  const { user } = useUser()
  const {
    upcomingTrips,
    inProgressTrips,
    cancelledTrips,
    pastTrips,
    isLoading,
  } = useTrips({
    user,
  })

  const {
    upcomingRentals,
    pastRentals,
    cancelledRentals,
    isLoading: isCarRentalLoading,
  } = useCarRentals({ user })

  const { t } = useTranslation()

  return (
    <div className="pt-180">
      <Head title={t('pages.trips.seoTitle')} />
      <main>
        <div className="container mb-40">
          <h1 className="text-30 md:text-40 font-bold leading-none mb-30">
            {t('pages.trips.title')}
          </h1>
          <Tabs
            aria-label={t('pages.trips.tabs')}
            tabContainerClassName="border-b border-dark-10 overflow-x-auto whitespace-nowrap"
            tabContentClassName="mb-30"
            tabs={[
              {
                // eslint-disable-next-line react/display-name
                title: ({ isSelected }) => (
                  <TabTitle isSelected={isSelected}>
                    {t('pages.trips.upcoming')}
                  </TabTitle>
                ),
                key: 'upcoming',
                content: (
                  <Trips
                    trips={[...inProgressTrips, ...upcomingTrips]}
                    rentals={upcomingRentals}
                    isLoading={isLoading && isCarRentalLoading}
                  />
                ),
              },
              {
                // eslint-disable-next-line react/display-name
                title: ({ isSelected }) => (
                  <TabTitle isSelected={isSelected}>
                    {t('pages.trips.past')}
                  </TabTitle>
                ),
                key: 'past',
                content: (
                  <Trips
                    trips={pastTrips}
                    isLoading={isLoading && isCarRentalLoading}
                    rentals={pastRentals}
                    isPast
                  />
                ),
              },
              {
                // eslint-disable-next-line react/display-name
                title: ({ isSelected }) => (
                  <TabTitle isSelected={isSelected}>
                    {t('pages.trips.cancelled')}
                  </TabTitle>
                ),
                key: 'cancelled',
                content: (
                  <Trips
                    trips={cancelledTrips}
                    rentals={cancelledRentals}
                    isLoading={isLoading && isCarRentalLoading}
                  />
                ),
              },
            ]}
          />
          <DownloadAppPanel trackingPrefix="booking_list" />
        </div>
      </main>
    </div>
  )
}

Bookings.layout = DefaultContentLayout
export default Bookings

export const getServerSideProps = composeSSR(getServerSideAuthProps)
